// Receipt item and charge editor functionality
import { logMessage, parseUrlForGroupId, formatCurrency } from './utils.mjs';
import { editExpense } from './api.mjs';
import { renderModal, closeModal } from './ui.mjs';
import { cloneItemizedData, createPayerItem } from './receiptHelpers.mjs';
import { createTextField } from './textInput.mjs';
import { appendModalFooterButton } from './uiHelpers.mjs';
import t from 'virtual:i18n';

// Unified modal for adding/editing items and charges
export async function openItemModal({
    expense,
    participants,
    mode = 'ADD', // 'ADD' | 'EDIT'
    targetType = 'ITEM', // 'ITEM' | 'CHARGE' | 'DISCOUNT' matches internal type
    existingItem = null,
    existingIndex = -1,
    onSave = null
}) {
    const modalAnchor = document.getElementById('modal-anchor');
    const isEdit = mode === 'EDIT';

    // Determine title
    let title = t.addNewItem;
    if (isEdit) {
        if (targetType === 'ITEM') title = t.editItem;
        else if (targetType === 'DISCOUNT') title = t.editDiscount;
        else title = t.editCharge;
    }

    // Initial Values
    let initialName = '';
    let initialPrice = '';
    let initialQty = '1';
    let initialAssignees = [];

    if (existingItem) {
        initialName = existingItem.name;
        // For charges, value might be negative if discount
        initialPrice = targetType === 'DISCOUNT' ? Math.abs(existingItem.value) : (existingItem.price || existingItem.value);
        initialQty = existingItem.quantity || 1;
        if (existingItem.assignees) {
            initialAssignees = JSON.parse(JSON.stringify(existingItem.assignees));
        }
    }

    // --- Form Construction ---
    const form = document.createElement('div');
    form.className = 'item-modal-form';
    Object.assign(form.style, {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        padding: '0 4px' // slight padding
    });

    // Type Selector (Only for Add)
    let typeSelect = null;
    if (!isEdit) {
        const typeLabel = document.createElement('label');
        typeLabel.textContent = t.type;
        typeLabel.style.fontSize = '12px';
        typeLabel.style.color = '#666';

        typeSelect = document.createElement('select');
        typeSelect.className = 'participant-name-input';
        typeSelect.innerHTML = `
            <option value="ITEM">${t.itemType}</option>
            <option value="DISCOUNT">${t.discountType}</option>
            <option value="CHARGE">${t.chargeType}</option>
        `;
        typeSelect.value = targetType;

        form.appendChild(typeLabel);
        form.appendChild(typeSelect);
    }

    // Name Input
    const nameWrapper = document.createElement('div');
    nameWrapper.innerHTML = `<label style="display:block; font-size:12px; color:#666; margin-bottom:4px;">${t.itemName}</label>`;
    const nameInput = createTextField({
        value: initialName,
        placeholder: t.egMilk,
        className: 'participant-name-input'
    });
    nameWrapper.appendChild(nameInput);
    form.appendChild(nameWrapper);

    // Price Input
    const currencySymbol = formatCurrency(0, expense.currency).replace(/\d|[.,\s]/g, '');
    const priceWrapper = document.createElement('div');
    priceWrapper.innerHTML = `<label style="display:block; font-size:12px; color:#666; margin-bottom:4px;">${t.amount} (${currencySymbol})</label>`;
    const priceInput = document.createElement('input');
    priceInput.type = 'number';
    priceInput.inputMode = 'decimal';
    priceInput.step = '0.01';
    priceInput.className = 'participant-name-input';
    priceInput.value = initialPrice;
    priceInput.placeholder = '0.00';
    priceWrapper.appendChild(priceInput);
    form.appendChild(priceWrapper);

    // Quantity Input (Only for Items)
    const qtyWrapper = document.createElement('div');
    qtyWrapper.style.display = 'none'; // Hidden by default
    qtyWrapper.innerHTML = `<label style="display:block; font-size:12px; color:#666; margin-bottom:4px;">${t.quantity}</label>`;
    const qtyInput = document.createElement('input');
    qtyInput.type = 'number';
    qtyInput.inputMode = 'numeric';
    qtyInput.min = '1';
    qtyInput.step = '1';
    qtyInput.className = 'participant-name-input';
    qtyInput.value = initialQty;
    qtyWrapper.appendChild(qtyInput);
    form.appendChild(qtyWrapper);

    // Assignees Section (Only for Items)
    const assigneesWrapper = document.createElement('div');
    assigneesWrapper.style.display = 'none';
    assigneesWrapper.innerHTML = `<label style="display:block; font-size:12px; color:#666; margin-bottom:4px;">${t.assignedTo}</label>`;
    const assigneesContainer = document.createElement('div');
    assigneesContainer.className = 'assignee-badges';
    assigneesContainer.style.flexWrap = 'wrap';
    assigneesContainer.style.gap = '8px';
    assigneesWrapper.appendChild(assigneesContainer);
    form.appendChild(assigneesWrapper);

    // Helper: Update Visibility based on type
    const updateVisibility = (type) => {
        if (type === 'ITEM') {
            qtyWrapper.style.display = 'block';
            assigneesWrapper.style.display = 'block';
            nameInput.placeholder = t.egMilk;
        } else {
            qtyWrapper.style.display = 'none';
            assigneesWrapper.style.display = 'none';
            nameInput.placeholder = type === 'DISCOUNT' ? t.egCoupon : t.egServiceFee;
        }
    };

    if (typeSelect) {
        typeSelect.addEventListener('change', () => updateVisibility(typeSelect.value));
        updateVisibility(typeSelect.value); // Initial
    } else {
        updateVisibility(targetType);
    }

    // --- Assignee Handling (Copy from existing logic) ---
    // We maintain a local copy of assignees: `currentAssignees`
    let currentAssignees = [...initialAssignees];

    const renderAssignees = () => {
        assigneesContainer.innerHTML = '';
        if (currentAssignees.length === 0) {
            const empty = document.createElement('span');
            empty.textContent = t.noOneAssigned;
            empty.style.color = '#999';
            empty.style.fontSize = '13px';
            empty.style.fontStyle = 'italic';
            assigneesContainer.appendChild(empty);
            return;
        }

        currentAssignees.forEach((assignee, idx) => {
            if (!assignee.participants || assignee.participants.length === 0) return;

            const totalQty = parseInt(qtyInput.value, 10) || 1;
            let names = assignee.participants.map(p => participants[p]).join(', ');
            if (totalQty > 1) {
                names = `(${assignee.quantity}) ${names}`;
            }

            const badge = document.createElement('span');
            badge.className = 'assignee-badge';
            badge.style.display = 'inline-flex';
            badge.style.alignItems = 'center';
            badge.style.gap = '6px';

            const text = document.createElement('span');
            text.textContent = names;
            badge.appendChild(text);

            const xBtn = document.createElement('span');
            xBtn.innerHTML = '&times;';
            xBtn.style.cursor = 'pointer';
            xBtn.style.fontSize = '16px';
            xBtn.style.lineHeight = '0.8';
            xBtn.style.opacity = '0.6';
            xBtn.onclick = (e) => {
                e.preventDefault();
                e.stopPropagation();

                // Logic to remove 1 qty or remove entire assignment if qty=1
                currentAssignees[idx].quantity -= 1;
                if (currentAssignees[idx].quantity <= 0) {
                    currentAssignees.splice(idx, 1);
                }
                renderAssignees();
                checkDirty(); // Trigger save button update
            };
            badge.appendChild(xBtn);
            assigneesContainer.appendChild(badge);
        });
    };
    renderAssignees();


    // --- Header & Actions ---
    const customHeader = document.createElement('div');
    customHeader.style.display = 'flex';
    customHeader.style.justifyContent = 'space-between';
    customHeader.style.alignItems = 'center';
    customHeader.style.width = '100%';

    const titleSpan = document.createElement('span');
    titleSpan.textContent = title;
    titleSpan.style.fontWeight = '600';
    titleSpan.style.fontSize = '18px';
    customHeader.appendChild(titleSpan);

    // Validations & Save
    const handleSave = async () => {
        const name = nameInput.value.trim();
        const price = parseFloat(priceInput.value);
        const type = typeSelect ? typeSelect.value : targetType;
        const qty = parseInt(qtyInput.value, 10);

        if (!name) { alert(t.enterNameAlert); return; }
        if (isNaN(price) || price < 0) { alert(t.enterPositiveAmountAlert); return; }
        if (type === 'ITEM' && (isNaN(qty) || qty < 1)) { alert(t.enterValidQuantityAlert); return; }

        const groupId = parseUrlForGroupId();
        if (!groupId) return;

        // Prepare Data
        const itemizedDataInput = cloneItemizedData(expense.itemizedData);

        if (isEdit) {
            // EDIT
            if (type === 'ITEM') {
                const item = itemizedDataInput.items[existingIndex];
                item.name = name;
                item.price = price;
                item.quantity = qty;
                item.assignees = currentAssignees;
            } else {
                const charge = itemizedDataInput.additionalCharges[existingIndex];
                charge.name = name;
                charge.value = price; // Charges are stored absolute usually? yes per existing code
            }
        } else {
            // ADD
            if (type === 'ITEM') {
                if (!itemizedDataInput.items) itemizedDataInput.items = [];
                itemizedDataInput.items.push({
                    id: Date.now().toString(),
                    name: name,
                    price: price,
                    quantity: qty,
                    assignees: []
                });
            } else {
                if (!itemizedDataInput.additionalCharges) itemizedDataInput.additionalCharges = [];
                itemizedDataInput.additionalCharges.push({
                    name: name,
                    value: price,
                    isDiscount: type === 'DISCOUNT'
                });
            }
        }

        try {
            logMessage(isEdit ? 'Updating...' : 'Adding...', 'info');
            const updatedGroup = await editExpense(groupId, expense.id, {
                itemizedData: itemizedDataInput,
                expenseType: expense.expenseType,
                currency: expense.currency || 'USD',
                date: expense.date,
                description: expense.description
            });
            logMessage('Saved successfully', 'success');
            const updatedExpense = updatedGroup.expenses.find(e => e.id === expense.id);
            if (updatedExpense) {
                const { renderReceiptView } = await import('./receiptView.mjs');
                renderReceiptView(updatedExpense, participants, false);
            }
            closeModal(modalContainer);
        } catch (e) {
            console.error(e);
            logMessage(`Error saving: ${e.message}`, 'error');
        }
    };

    const modalContainer = renderModal({
        anchorEl: modalAnchor,
        title: customHeader,
        body: form,
        buttons: []
    });

    // Create fixed button at bottom
    const { createPrimaryButton } = await import('./components.mjs');

    const saveBtn = createPrimaryButton({
        text: isEdit ? t.saveChanges : t.add,
        disabled: true,
        onClick: handleSave
    });

    // Function to check if button should be enabled
    const checkDirty = () => {
        const currentName = nameInput.value.trim();
        const currentPrice = parseFloat(priceInput.value);
        const currentQty = parseInt(qtyInput.value, 10);
        let shouldEnable = false;

        if (isEdit) {
            if (currentName !== initialName) shouldEnable = true;
            if (currentPrice !== parseFloat(initialPrice)) shouldEnable = true;
            if (targetType === 'ITEM' && currentQty !== parseInt(initialQty, 10)) shouldEnable = true;
            // Assignees check for items
            if (targetType === 'ITEM') {
                if (JSON.stringify(currentAssignees) !== JSON.stringify(initialAssignees)) shouldEnable = true;
            }
        } else {
            // For Add, just check if valid (non-empty)
            if (currentName && !isNaN(currentPrice) && currentPrice >= 0) shouldEnable = true;
        }

        saveBtn.disabled = !shouldEnable;
        saveBtn.style.opacity = shouldEnable ? '1' : '0.5';
        saveBtn.style.cursor = shouldEnable ? 'pointer' : 'not-allowed';
    };

    let lastQtyValue = qtyInput.value;
    nameInput.addEventListener('input', checkDirty);
    priceInput.addEventListener('input', checkDirty);
    qtyInput.addEventListener('input', () => {
        const newVal = qtyInput.value;
        if (targetType === 'ITEM' && newVal !== lastQtyValue) {
            currentAssignees = [];
            lastQtyValue = newVal;
        }
        checkDirty();
        renderAssignees();
    });
    if (typeSelect) typeSelect.addEventListener('change', checkDirty);

    appendModalFooterButton(modalContainer, saveBtn);

    // Delete Icon (Only Edit Mode)
    if (isEdit) {
        const trashIcon = document.createElement('div');
        trashIcon.innerHTML = `
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" class="trash-icon" stroke="#dc3545" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
            </svg>
        `;
        trashIcon.style.cursor = 'pointer';
        trashIcon.style.padding = '8px';
        trashIcon.style.marginRight = '-8px';
        trashIcon.onclick = () => {
            if (confirm(t.deleteItemConfirm.replace('{name}', initialName))) {
                // Close the specific modal
                if (modalContainer) closeModal(modalContainer);

                // Determine if we're deleting item or charge
                if (targetType === 'ITEM') {
                    deleteItem(expense, existingIndex, participants);
                } else {
                    deleteCharge(expense, existingIndex, participants);
                }
            }
        };
        customHeader.appendChild(trashIcon);
    }

    // Initial state
    checkDirty();
}

// Wrapper for editing a charge
export function editCharge(charge, index, expense, participants) {
    openItemModal({
        expense,
        participants,
        mode: 'EDIT',
        targetType: charge.isDiscount ? 'DISCOUNT' : 'CHARGE',
        existingItem: charge,
        existingIndex: index
    });
}

// Delete a charge
export async function deleteCharge(expense, index, participants) {
    const groupId = parseUrlForGroupId();
    if (!groupId) return;

    const itemizedDataInput = cloneItemizedData(expense.itemizedData);

    // Remove logic
    itemizedDataInput.additionalCharges.splice(index, 1);

    try {
        logMessage('Deleting charge...', 'info');
        const updatedGroup = await editExpense(groupId, expense.id, {
            itemizedData: itemizedDataInput,
            expenseType: expense.expenseType,
            currency: expense.currency || 'USD',
            date: expense.date,
            description: expense.description
        });
        logMessage('Charge deleted', 'success');
        const updatedExpense = updatedGroup.expenses.find(e => e.id === expense.id);
        if (updatedExpense) {
            const { renderReceiptView } = await import('./receiptView.mjs');
            renderReceiptView(updatedExpense, participants, false);
        }
    } catch (e) {
        console.error(e);
        logMessage(`Error deleting charge: ${e.message}`, 'error');
    }
}

// Wrapper for editing an item
export function editItem(item, index, expense, participants) {
    openItemModal({
        expense,
        participants,
        mode: 'EDIT',
        targetType: 'ITEM',
        existingItem: item,
        existingIndex: index
    });
}

// Edit expense details (Title and Date)
export async function editExpenseDetails(expense, participants) {
    const modalAnchor = document.getElementById('modal-anchor');
    const { createPrimaryButton } = await import('./components.mjs');

    // Store original values
    const originalDescription = expense.description;
    const originalDate = expense.date ? new Date(expense.date).toISOString().split('T')[0] : '';

    const form = document.createElement('div');
    form.className = 'edit-expense-form';
    form.style.display = 'flex';
    form.style.flexDirection = 'column';
    form.style.gap = '15px';

    // Payer Row (Immediate update style)
    const payerRow = document.createElement('div');
    payerRow.className = 'paid-by-row';
    const hasPayer = expense.payers && expense.payers.length > 0;
    
    if (hasPayer) {
        const payerName = participants[expense.payers[0]];
        payerRow.innerHTML = `<span style="font-size:14px; font-weight:500; color:#555;">${t.paidByText}</span> <span class="payer-link" style="color:var(--color-primary); font-weight:700; font-size:16px; cursor:pointer; text-decoration:underline;">${payerName}</span>`;
    } else {
        payerRow.innerHTML = `<span class="payer-link" style="color:var(--color-primary); font-weight:700; font-size:16px; cursor:pointer; text-decoration:underline;">${t.whoPaidForIt}</span>`;
    }
    
    payerRow.querySelector('.payer-link').onclick = (e) => {
        e.stopPropagation();
        closeModal(modal);
        openPayerSelector(expense, participants);
    };

    // Description Input
    const descLabel = document.createElement('label');
    descLabel.textContent = t.receiptName;
    descLabel.style.display = 'block';
    descLabel.style.fontSize = '12px';
    descLabel.style.color = '#666';
    descLabel.style.marginBottom = '4px';

    const descInput = createTextField({
        value: expense.description,
        className: 'participant-name-input',
        onSubmit: () => saveButton.click()
    });

    // Date Input
    const dateLabel = document.createElement('label');
    dateLabel.textContent = t.date;
    dateLabel.style.display = 'block';
    dateLabel.style.fontSize = '12px';
    dateLabel.style.color = '#666';
    dateLabel.style.marginBottom = '4px';

    const dateInput = document.createElement('input');
    dateInput.type = 'date';
    // Ensure date is in YYYY-MM-DD
    if (expense.date) {
        try {
            dateInput.value = originalDate;
        } catch (e) {
            console.error('Error parsing date for input', e);
        }
    }
    dateInput.className = 'participant-name-input';

    form.appendChild(descLabel);
    form.appendChild(descInput);
    form.appendChild(dateLabel);
    form.appendChild(dateInput);
    form.appendChild(payerRow);

    const modal = renderModal({
        anchorEl: modalAnchor,
        title: t.editReceiptDetails,
        body: form,
        buttons: []
    });


    const saveButton = createPrimaryButton({
        text: t.save,
        disabled: true,
        onClick: async () => {
            const newDesc = descInput.value.trim();
            const newDate = dateInput.value;

            if (!newDesc) { alert('Please enter a name'); return; }
            if (!newDate) { alert('Please enter a date'); return; }

            const groupId = parseUrlForGroupId();
            if (!groupId) return;

            try {
                logMessage('Updating receipt details...', 'info');
                const itemizedDataInput = cloneItemizedData(expense.itemizedData);

                const updatedGroup = await editExpense(groupId, expense.id, {
                    itemizedData: itemizedDataInput,
                    expenseType: expense.expenseType,
                    currency: expense.currency || 'USD',
                    date: newDate,
                    description: newDesc
                });
                logMessage('Receipt details updated', 'success');
                const updatedExpense = updatedGroup.expenses.find(e => e.id === expense.id);
                if (updatedExpense) {
                    const { renderReceiptView } = await import('./receiptView.mjs');
                    renderReceiptView(updatedExpense, participants, false);
                }
                closeModal(modal);
            } catch (e) {
                console.error(e);
                logMessage(`Error updating details: ${e.message}`, 'error');
            }
        }
    });

    function updateButtonState() {
        const currentDesc = descInput.value.trim();
        const currentDate = dateInput.value;
        const hasChanged = (currentDesc !== originalDescription || currentDate !== originalDate) && currentDesc.length > 0;
        saveButton.disabled = !hasChanged;
        saveButton.style.opacity = hasChanged ? '1' : '0.5';
        saveButton.style.cursor = hasChanged ? 'pointer' : 'not-allowed';
    }

    descInput.addEventListener('input', updateButtonState);
    dateInput.addEventListener('change', updateButtonState);
    appendModalFooterButton(modal, saveButton);
    updateButtonState();
}

// Wrapper for adding a new item
export function addItem(expense, participants) {
    openItemModal({
        expense,
        participants,
        mode: 'ADD'
    });
}

// Open modal to select who paid
export async function openPayerSelector(expense, participants, onSelect = null) {
    const { openPayerSelectorDrawer } = await import('./payerSelector.mjs');
    
    openPayerSelectorDrawer({
        amount: expense.amount,
        currency: expense.currency || 'USD',
        participants: participants,
        payers: expense.payers || [],
        payerAmounts: expense.payerAmounts || [],
        onSave: async (newPayers, newPayerAmounts, updatedParticipants) => {
            if (onSelect) {
                if (newPayers.length > 0) {
                    onSelect(newPayers[0]);
                }
                return;
            }

            const groupId = parseUrlForGroupId();
            if (!groupId) return;

            const itemizedDataInput = cloneItemizedData(expense.itemizedData);

            try {
                logMessage('Updating payers...', 'info');
                const updatedGroup = await editExpense(groupId, expense.id, {
                    payers: newPayers,
                    payerAmounts: newPayerAmounts,
                    itemizedData: itemizedDataInput,
                    expenseType: expense.expenseType,
                    currency: expense.currency || 'USD',
                    date: expense.date,
                    description: expense.description
                }, updatedParticipants);

                logMessage('Payers updated successfully', 'success');

                const updatedExpense = updatedGroup.expenses.find(e => e.id === expense.id);
                if (updatedExpense) {
                    const { renderReceiptView } = await import('./receiptView.mjs');
                    renderReceiptView(updatedExpense, updatedGroup.participants, false);
                }
            } catch (e) {
                console.error(e);
                logMessage(`Error updating payers: ${e.message}`, 'error');
            }
        }
    });
}

// Delete an item from the receipt
export async function deleteItem(expense, itemIndex, participants) {
    const groupId = parseUrlForGroupId();
    if (!groupId) {
        logMessage('Cannot determine group ID', 'error');
        return;
    }

    // Deep copy and strip typename
    const itemizedDataInput = cloneItemizedData(expense.itemizedData);

    // Remove the item
    itemizedDataInput.items.splice(itemIndex, 1);

    try {
        logMessage('Deleting item...', 'info');
        const updatedGroup = await editExpense(groupId, expense.id, {
            itemizedData: itemizedDataInput,
            expenseType: expense.expenseType,
            currency: expense.currency || 'USD',
            date: expense.date,
            description: expense.description
        });

        logMessage('Item deleted successfully', 'success');

        const updatedExpense = updatedGroup.expenses.find(e => e.id === expense.id);
        if (updatedExpense) {
            const { renderReceiptView } = await import('./receiptView.mjs');
            renderReceiptView(updatedExpense, participants, false); // Don't reset selections
        }

    } catch (e) {
        console.error(e);
        logMessage(`Error deleting item: ${e.message}`, 'error');
    }
}
